/**
 * `markdown.ts` — the ONE Markdown parser this app has, as plain data.
 *
 * It produces an AST and renders nothing. Two consumers read it:
 *
 *   - `components/controls/MarkdownView.vue` — the DOM rendering (the AI debug
 *     panel's prompt previews, and a workflow step's `step_instructions` on the
 *     play panel and its XR slate).
 *   - `models/utils/markdownSpeech.ts` — the SPOKEN rendering, for a workflow
 *     script's `say` line.
 *
 * **The split exists so the two cannot disagree about what is markup.** The
 * learner reads the panel and hears the line; if one surface reads `**` as
 * emphasis and the other reads it as two characters to pronounce, they are
 * showing and saying different lessons. That is not hypothetical — it is the
 * bug this module was extracted for (2026-08-31): the say path passed the
 * markup through untouched and the realtime model, told to speak the line
 * verbatim, sometimes read the asterisks out. A second, "simpler" stripper next
 * to the renderer would have been the same class of drift one step later, which
 * is why the parser moved rather than being copied.
 *
 * ## The dialect, and why it is hand-written rather than `markdown-it`
 *
 * The corpus is known and small: `public/AI/**` (headings, `-` bullets nested up
 * to three levels, one ordered list, `**bold**`, `` `code` ``), the published
 * workflow schema (fenced code, tables), and a workflow author's
 * `step_instructions`. A parser scoped to exactly that is ~250 lines, ships
 * nothing, and cannot be handed a construct it silently mis-renders without
 * someone noticing here first. Reach for a real Markdown library the day this is
 * pointed at genuinely arbitrary markdown.
 *
 * Two deliberate omissions, both about NOT breaking the corpus:
 *
 *   - **`_` never marks emphasis**, only `*` and `**`. Tool ids are the single
 *     most common inline token in these files, and `go_to_scene` under
 *     CommonMark's intraword rules is a trap this does not need to implement its
 *     way out of: underscores are literal, full stop.
 *   - **No indented code blocks.** Four-space indentation inside a list is how
 *     the prompts continue an item, and reading it as code would shred every
 *     nested bullet in the corpus. Fenced code is supported and is what the
 *     schema actually uses.
 *
 * Inline markers are PAIRED-only: a lone `*` with no partner is literal text, so
 * `2 * 3` and `5*` survive a round trip untouched. That is what lets ordinary
 * prose pass through both renderers unchanged.
 *
 * A leading `---` fence is a FRONTMATTER block rather than a thematic break plus
 * stray text — the debug panel previews whole files as well as bodies, and a
 * role's frontmatter is data the reader wants set apart, not prose.
 *
 * ⚠ Pure and DOM-free by contract. The renderer imports this; this must never
 * import the renderer, `vue`, or anything under `src/components/`.
 */

/** A block-level construct. `parseMarkdownBlocks` returns a flat list of these. */
export type MdBlock =
  | { readonly type: "frontmatter"; readonly text: string }
  | { readonly type: "heading"; readonly level: number; readonly text: string }
  | { readonly type: "paragraph"; readonly text: string }
  | { readonly type: "code"; readonly lang: string; readonly text: string }
  | { readonly type: "rule" }
  | { readonly type: "quote"; readonly blocks: MdBlock[] }
  | { readonly type: "list"; readonly ordered: boolean; readonly items: MdBlock[][] }
  | { readonly type: "table"; readonly head: string[]; readonly rows: string[][] };

/**
 * An inline construct, from {@link parseMarkdownInline}.
 *
 * A `link` keeps its raw `href` rather than being pre-vetted here: whether a
 * scheme is safe is a question about the DOM attribute it is about to become, so
 * the renderer that creates that attribute owns the allow-list. A speech
 * renderer never touches the href at all.
 */
export type MdInline =
  | { readonly type: "text"; readonly text: string }
  | { readonly type: "break" }
  | { readonly type: "code"; readonly text: string }
  | { readonly type: "strong"; readonly children: MdInline[] }
  | { readonly type: "em"; readonly children: MdInline[] }
  | { readonly type: "del"; readonly children: MdInline[] }
  | { readonly type: "link"; readonly href: string; readonly children: MdInline[] };

/** `- `, `* `, `+ `, `1. `, `1) ` — with where the item's own content starts. */
interface ListMarker {
  readonly indent: number;
  readonly ordered: boolean;
  /** Column the content begins at, used to dedent the item's continuation lines. */
  readonly contentIndent: number;
  readonly content: string;
}

const K_LIST_ITEM = /^(\s*)([-*+]|\d{1,9}[.)])(\s+)(.*)$/;
const K_HEADING = /^(#{1,6})\s+(.*?)\s*#*\s*$/;
const K_FENCE = /^\s{0,3}(`{3,}|~{3,})\s*([^`]*)$/;
const K_RULE = /^\s{0,3}(?:-{3,}|_{3,}|\*{3,})\s*$/;
const K_TABLE_DELIMITER = /^\s*\|?[\s:|-]*-[\s:|-]*\|?\s*$/;
const K_LINK = /^\[([^\]]*)\]\(\s*([^)\s]*)(?:\s+"[^"]*")?\s*\)/;

/* ----------------------------------------------------------------- blocks -- */

/**
 * Parse a Markdown document into blocks.
 *
 * Returns an EMPTY list for blank input, so a caller never has to distinguish
 * "nothing to render" from "one empty paragraph".
 */
export function parseMarkdownBlocks(source: string): MdBlock[] {
  const text = (source ?? "").replace(/\r\n?/g, "\n");
  if (!text.trim()) return [];

  const lines = text.split("\n");
  const blocks: MdBlock[] = [];

  // Frontmatter is a TOP-OF-DOCUMENT construct only. Anything that looks like a
  // fence further down is a thematic break, which is what the same `---` means
  // once prose has started.
  let start = 0;
  while (start < lines.length && !lines[start].trim()) start++;
  if (lines[start]?.trim() === "---") {
    for (let i = start + 1; i < lines.length; i++) {
      if (lines[i].trim() !== "---") continue;
      blocks.push({ type: "frontmatter", text: lines.slice(start + 1, i).join("\n") });
      start = i + 1;
      break;
    }
  }

  blocks.push(...parseBlocks(lines.slice(start)));
  return blocks;
}

function parseBlocks(lines: readonly string[]): MdBlock[] {
  const out: MdBlock[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (!line.trim()) {
      i++;
      continue;
    }

    const fence = K_FENCE.exec(line);
    if (fence) {
      const marker = fence[1];
      const body: string[] = [];
      i++;
      while (i < lines.length && lines[i].trim() !== marker && !lines[i].trim().startsWith(marker)) {
        body.push(lines[i]);
        i++;
      }
      // An unterminated fence runs to the end of the document, which is what
      // every Markdown implementation does and is the right reading of a file
      // that was cut off mid-block.
      if (i < lines.length) i++;
      out.push({ type: "code", lang: fence[2].trim(), text: body.join("\n") });
      continue;
    }

    const heading = K_HEADING.exec(line);
    if (heading) {
      out.push({ type: "heading", level: heading[1].length, text: heading[2] });
      i++;
      continue;
    }

    // Tested BEFORE the list, so `---` is never read as a `-` bullet with an
    // empty marker, and before the table so a delimiter row on its own is a rule.
    if (K_RULE.test(line)) {
      out.push({ type: "rule" });
      i++;
      continue;
    }

    if (line.trim().startsWith(">")) {
      const quoted: string[] = [];
      while (i < lines.length && (lines[i].trim().startsWith(">") || (quoted.length > 0 && lines[i].trim()))) {
        quoted.push(lines[i].replace(/^\s*>\s?/, ""));
        i++;
      }
      out.push({ type: "quote", blocks: parseBlocks(quoted) });
      continue;
    }

    if (line.trim().startsWith("|") && i + 1 < lines.length && K_TABLE_DELIMITER.test(lines[i + 1])) {
      const head = splitTableRow(line);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && lines[i].trim().startsWith("|")) {
        rows.push(splitTableRow(lines[i]));
        i++;
      }
      out.push({ type: "table", head, rows });
      continue;
    }

    const marker = matchListItem(line);
    if (marker) {
      const list = parseList(lines, i, marker);
      out.push(list.block);
      i = list.next;
      continue;
    }

    // Paragraph: run to the next blank line or the next block-level construct.
    const paragraph: string[] = [];
    while (i < lines.length && lines[i].trim() && !startsBlock(lines[i], lines[i + 1])) {
      paragraph.push(lines[i].trim());
      i++;
    }
    if (paragraph.length === 0) {
      // Defensive: `startsBlock` was true for the very first line, which the
      // branches above should already have claimed. Consume it as text rather
      // than spinning.
      paragraph.push(lines[i].trim());
      i++;
    }
    out.push({ type: "paragraph", text: paragraph.join("\n") });
  }

  return out;
}

/** Whether a line would open a new block, i.e. ends the paragraph before it. */
function startsBlock(line: string, next: string | undefined): boolean {
  if (K_FENCE.test(line)) return true;
  if (K_HEADING.test(line)) return true;
  if (K_RULE.test(line)) return true;
  if (line.trim().startsWith(">")) return true;
  if (matchListItem(line) !== null) return true;
  if (line.trim().startsWith("|") && next !== undefined && K_TABLE_DELIMITER.test(next)) return true;
  return false;
}

function matchListItem(line: string): ListMarker | null {
  const m = K_LIST_ITEM.exec(line);
  if (!m) return null;
  // `---` and `***` are thematic breaks, not one-item lists; the caller tests
  // for those first, and this second guard keeps `matchListItem` honest for the
  // `startsBlock` lookahead, which has no such ordering.
  if (K_RULE.test(line)) return null;
  const indent = m[1].length;
  return {
    indent,
    ordered: /\d/.test(m[2]),
    contentIndent: indent + m[2].length + m[3].length,
    content: m[4],
  };
}

/**
 * One list, from its first item to the first line that is neither an item at its
 * own indent nor indented under one.
 *
 * A nested list is NOT parsed here — its lines are simply more-indented, so they
 * are dedented into the owning item and recursed on. That is what keeps three
 * levels of `- ` working with one code path instead of three.
 */
function parseList(
  lines: readonly string[],
  from: number,
  first: ListMarker,
): { block: MdBlock; next: number } {
  const items: string[][] = [];
  let current: string[] = [];
  let currentContentIndent = first.contentIndent;
  let started = false;
  let i = from;

  while (i < lines.length) {
    const line = lines[i];

    if (!line.trim()) {
      // A blank line ends the list unless something still belongs to it: another
      // item at this level, or a continuation indented under the current one.
      let j = i;
      while (j < lines.length && !lines[j].trim()) j++;
      if (j >= lines.length) break;
      const ahead = matchListItem(lines[j]);
      const continues =
        (ahead !== null && ahead.indent <= first.indent && ahead.ordered === first.ordered) ||
        indentOf(lines[j]) >= currentContentIndent;
      if (!continues) break;
      current.push("");
      i++;
      continue;
    }

    const marker = matchListItem(line);
    if (marker && marker.indent <= first.indent) {
      // A different marker KIND at this level is a different list.
      if (marker.ordered !== first.ordered) break;
      if (started) items.push(current);
      current = [marker.content];
      currentContentIndent = marker.contentIndent;
      started = true;
      i++;
      continue;
    }

    const indent = indentOf(line);
    if (indent >= currentContentIndent || marker !== null) {
      current.push(line.slice(Math.min(indent, currentContentIndent)));
      i++;
      continue;
    }

    // Lazy continuation: an unindented line directly under an item's text is
    // still that item's paragraph. A blank line above would have ended the list.
    current.push(line.trim());
    i++;
  }

  if (started) items.push(current);
  return {
    block: { type: "list", ordered: first.ordered, items: items.map((item) => parseBlocks(item)) },
    next: i,
  };
}

function indentOf(line: string): number {
  return (/^\s*/.exec(line)?.[0] ?? "").length;
}

/** `| a | b |` → `["a", "b"]`, tolerating a missing leading or trailing pipe. */
function splitTableRow(line: string): string[] {
  const trimmed = line.trim().replace(/^\|/, "").replace(/\|$/, "");
  return trimmed.split("|").map((cell) => cell.trim());
}

/* ----------------------------------------------------------------- inline -- */

/**
 * Inline scan. Left-to-right, one character at a time, with `` ` `` claimed
 * FIRST so `**` inside a code span stays literal — the published schema is full
 * of `` `**` ``-adjacent punctuation and a regex pass ordered the other way
 * turns half of it into bold.
 *
 * Every marker is PAIRED: an opener with no partner falls through to the text
 * buffer, so a lone `*` is a character and not the start of anything. Runs of
 * plain text are buffered and emitted as one `text` node, which is what makes
 * prose with no markup at all come back byte-identical.
 */
export function parseMarkdownInline(text: string): MdInline[] {
  const out: MdInline[] = [];
  let buffer = "";
  let i = 0;

  const flush = (): void => {
    if (buffer) {
      out.push({ type: "text", text: buffer });
      buffer = "";
    }
  };

  while (i < text.length) {
    const ch = text[i];

    if (ch === "\\" && i + 1 < text.length) {
      buffer += text[i + 1];
      i += 2;
      continue;
    }

    if (ch === "\n") {
      // A soft break inside a paragraph. Kept as a real break rather than
      // collapsed to a space: these are prompts, and a rule per line is how they
      // are written — reflowing them into one block changes how the document
      // reads far more than it changes how the model reads it.
      flush();
      out.push({ type: "break" });
      i++;
      continue;
    }

    if (ch === "`") {
      const end = text.indexOf("`", i + 1);
      if (end > i + 1) {
        flush();
        out.push({ type: "code", text: text.slice(i + 1, end) });
        i = end + 1;
        continue;
      }
    }

    if (ch === "*" && text[i + 1] === "*") {
      const end = text.indexOf("**", i + 2);
      if (end > i + 1) {
        flush();
        out.push({ type: "strong", children: parseMarkdownInline(text.slice(i + 2, end)) });
        i = end + 2;
        continue;
      }
    }

    if (ch === "*") {
      const end = text.indexOf("*", i + 1);
      if (end > i + 1) {
        flush();
        out.push({ type: "em", children: parseMarkdownInline(text.slice(i + 1, end)) });
        i = end + 1;
        continue;
      }
    }

    if (ch === "~" && text[i + 1] === "~") {
      const end = text.indexOf("~~", i + 2);
      if (end > i + 1) {
        flush();
        out.push({ type: "del", children: parseMarkdownInline(text.slice(i + 2, end)) });
        i = end + 2;
        continue;
      }
    }

    if (ch === "[") {
      const link = K_LINK.exec(text.slice(i));
      if (link) {
        flush();
        out.push({ type: "link", href: link[2], children: parseMarkdownInline(link[1]) });
        i += link[0].length;
        continue;
      }
    }

    buffer += ch;
    i++;
  }

  flush();
  return out;
}
